import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Button,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { reportAPI } from '../services/api';
import { ReportTarget } from '../types/api';
import { fetchReportTargets, readCachedReportTargets } from '../features/auth/utils/reportTargetCache';

interface ChooseReportTargetPageProps {
  onSelected: (target: ReportTarget) => void;
  canManageSpirisConnections?: boolean;
  onManageSpirisConnections?: () => void;
}

const ChooseReportTargetPage: React.FC<ChooseReportTargetPageProps> = ({
  onSelected,
  canManageSpirisConnections = false,
  onManageSpirisConnections,
}) => {
  const theme = useTheme();
  const [targets, setTargets] = useState<ReportTarget[]>([]);
  const [isLoadingTargets, setIsLoadingTargets] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cachedTargets = readCachedReportTargets(true);
    if (cachedTargets) {
      setTargets(cachedTargets);
      setIsLoadingTargets(false);
    }

    const loadTargets = async () => {
      try {
        if (!cachedTargets) {
          setIsLoadingTargets(true);
        }
        setError(null);
        const nextTargets = await fetchReportTargets({ forceRefresh: true });
        setTargets(nextTargets);
      } catch (err) {
        if ((err as { suppressUserError?: boolean } | null)?.suppressUserError) {
          return;
        }
        if (!cachedTargets) {
          setError(err instanceof Error ? err.message : 'Kunde inte ladda.');
        }
      } finally {
        setIsLoadingTargets(false);
      }
    };

    loadTargets();
  }, []);

  const handleSelect = async (target: ReportTarget) => {
    if (!target?.id || !target?.type) {
      setError('Välj mål.');
      return;
    }
    try {
      setError(null);
      await reportAPI.setTarget({ id: target.id, type: target.type });
      localStorage.setItem('all_access_target_selected', `${target.type}:${target.id}`);
      if (target.type === 'company') {
        localStorage.setItem('all_access_profile_selected', target.id);
      }
      onSelected(target);
    } catch (err) {
      if ((err as { suppressUserError?: boolean } | null)?.suppressUserError) {
        return;
      }
      setError(err instanceof Error ? err.message : 'Byte misslyckades.');
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        width: '100%',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.mode === 'dark'
          ? theme.palette.background.default
          : theme.palette.background.paper,
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={6}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: '16px',
            background: theme.palette.background.paper,
            backdropFilter: 'blur(10px)',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center', mb: 1 }}>
            Välj rapportmål
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', color: theme.palette.text.secondary, mb: 3 }}>
            Välj bolag eller koncern för att fortsätta.
          </Typography>

          {isLoadingTargets ? (
            <List sx={{ mb: 2 }}>
              {Array.from({ length: 5 }).map((_, idx) => (
                <ListItem key={idx} disablePadding sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: '100%',
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      background: theme.palette.background.paper,
                    }}
                  >
                    <Skeleton variant="text" width="60%" height={22} />
                    <Skeleton variant="text" width="30%" height={16} />
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <List sx={{ mb: 2 }}>
              {targets.map((target) => (
                <ListItem key={`${target.type}:${target.id}`} disablePadding>
                  <ListItemButton
                    onClick={() => handleSelect(target)}
                    disabled={isLoadingTargets}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      background: theme.palette.background.paper,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
                      },
                    }}
                  >
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {target.label}
                          </Typography>
                        </Box>
                      )}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          {error && (
            <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 1 }}>
              {error}
            </Typography>
          )}

          {!isLoadingTargets && targets.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: canManageSpirisConnections ? 2 : 0 }}>
                Inga tillgangliga rapportmal.
              </Typography>
              {canManageSpirisConnections && onManageSpirisConnections && (
                <Button variant="contained" onClick={onManageSpirisConnections}>
                  Hantera Spiris-kopplingar
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </Container>

    </Box>
  );
};

export default ChooseReportTargetPage;
